import PropTypes from 'prop-types';
import React, { memo, useCallback, useRef, useState } from 'react';
import { List } from 'immutable';
import classNames from 'classnames';
import { ResizableBox } from 'react-resizable';

import { IconButton } from '../../helpers/IconButton';
import { CompanyDealNote } from '../CompanyDealNote';

import style from './style.module.css';

const COLLAPSED_WIDTH = 32;

/**
 * Component is used to display list of deal notes.
 *
 * @param props.onMouseEnter {Function} OnMouseEnter callback. 1st arg is event. 2nd is deal note's eventId.
 * @param props.onMouseLeave {Function} OnMouseLeave callback. 1st arg is event. 2nd is deal note's eventId.
 * @param props.onToggle {Function} Toggle deal note section.
 * @param props.isClosed {Boolean} Set to true will close note section.
 * @param props.dealNotes {Immutable.List} List of Maps, that should describe note. #content {string} is note content.
 * #[isMarked=false] {boolean} uf `true` append .text-bold class to element. #eventId is deal note's eventId.
 * @returns {XML}
 */
const CompanyDealNotesComponent = ({
  onEditEvent,
  onMouseEnter,
  onMouseLeave,
  onToggle,
  isClosed,
  dealNotes,
  dealNoteSize,
  onChange,
}) => {
  const panelRef = useRef();
  const [openWidth, setOpenWidth] = useState(dealNoteSize.width);
  const [isResizing, setIsResizing] = useState(false);

  const handleResizeStart = useCallback(() => {
    setIsResizing(true);
  }, []);

  const handleResizeStop = useCallback(
    (event, data) => {
      setIsResizing(false);
      setOpenWidth(data.size.width);

      if (onChange) {
        onChange({ target: { name: 'dealNotes', value: data } });
      }
    },
    [onChange],
  );

  const nonEmptyNotes = dealNotes.filter(note => note.get('content') !== '<p><br></p>'); // Check if the wysiwyg text not empty.

  const content =
    nonEmptyNotes.size === 0 ? (
      <p>No rows to display</p>
    ) : (
      nonEmptyNotes.map(note => (
        <CompanyDealNote
          key={note.get('eventId')}
          note={note}
          onEditEvent={onEditEvent}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
        />
      ))
    );
  const boxProps = {
    axis: 'x',
    minConstraints: isClosed ? [COLLAPSED_WIDTH, dealNoteSize.height] : dealNoteSize.minConstraints,
    maxConstraints: isClosed ? [COLLAPSED_WIDTH, dealNoteSize.height] : [window.innerWidth - 100, dealNoteSize.height],
    width: isClosed ? COLLAPSED_WIDTH : openWidth,
    height: dealNoteSize.height,
  };
  const contentStyle = isResizing ? undefined : { width: openWidth - COLLAPSED_WIDTH };

  return (
    <ResizableBox
      {...boxProps}
      className={classNames(style.host, { [style.closed]: isClosed, [style.resizing]: isResizing })}
      onResizeStart={handleResizeStart}
      onResizeStop={handleResizeStop}
    >
      <>
        <IconButton
          className={style.toggle}
          icon={isClosed ? 'chevron-right' : 'chevron-left'}
          onClick={onToggle}
          title={isClosed ? 'Show deal notes' : 'Hide deal notes'}
          active
        />
        <div ref={panelRef} className={style.content} style={contentStyle}>
          {content}
        </div>
      </>
    </ResizableBox>
  );
};

CompanyDealNotesComponent.propTypes = {
  dealNotes: PropTypes.instanceOf(List).isRequired,
  dealNoteSize: PropTypes.object.isRequired,
  isClosed: PropTypes.bool.isRequired,
  onChange: PropTypes.func,
  onMouseEnter: PropTypes.func.isRequired,
  onMouseLeave: PropTypes.func.isRequired,
  onToggle: PropTypes.func.isRequired,
};

/** Company deal notes component. */
export const CompanyDealNotes = memo(CompanyDealNotesComponent);
